import { CreateSphereDialogProps } from "@components/common/dialog/sphere-dialog-provider";
import { RoleDisplayNames } from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { Typography } from "@mui/material";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  addProcessingProjects,
  removeProcessingProjects,
} from "@store/projects/projects-slice";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { AppDispatch } from "@store/store-helper";
import { ProjectEvents } from "@utils/track-event/track-event-list";

interface ArchiveProjectDialogParams {
  /** The project to archive or unarchive. Must include id and archivingState to determine the current state. */
  project: Pick<SdbProject, "id" | "archivingState">;
  /** Function to create and display a confirmation dialog. Returns true if user confirms, false if cancelled. */
  createDialog: (
    options: CreateSphereDialogProps,
    content?: JSX.Element | string
  ) => Promise<boolean>;
  /** Redux dispatch function to trigger project update actions. */
  dispatch: AppDispatch;
  /** Function to track analytics events for project archive/unarchive actions. */
  trackEvent: (params: { name: string }) => void;
}

/**
 * Shared logic to show archive/unarchive confirmation dialog and execute the action.
 * Used by both useProjectArchiveDialog hook and mobile menu.
 */
export async function showArchiveProjectDialog({
  project,
  createDialog,
  dispatch,
  trackEvent,
}: ArchiveProjectDialogParams): Promise<boolean> {
  const isProjectActive = project.archivingState === "UNARCHIVED";

  // https://help.holobuilder.com/en/articles/3435523-how-to-archive-a-project-in-the-dashboard
  const whoCanViewArchived = [
    `${RoleDisplayNames.COMPANY_EXECUTIVE}s,`,
    `${RoleDisplayNames.COMPANY_MANAGER}s and`,
    `${RoleDisplayNames.PROJECT_MANAGER}s of this project`,
  ].join(" ");

  // Creates a modal dialog and waits for the user to perform an action
  const hasConfirmed = await createDialog(
    {
      title: isProjectActive ? "Archive Project" : "Unarchive Project",
      confirmText: isProjectActive ? "Archive" : "Unarchive",
      isHideClose: true,
    },
    <>
      <Typography>
        Do you really want to {isProjectActive ? "archive" : "unarchive"} this
        project?
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          mt: "16px",
        }}
      >
        {isProjectActive
          ? `In archived state, only ${whoCanViewArchived} will be able to view the project online.`
          : "Afterwards, all project members will be able to access the project again."}
      </Typography>
    </>
  );

  if (hasConfirmed) {
    trackEvent({
      name: isProjectActive
        ? ProjectEvents.archiveProject
        : ProjectEvents.unarchiveProject,
    });

    // Add the projectId to processing projects to start the loading spinner in the project
    dispatch(addProcessingProjects([project.id]));

    const payload: FaroCoreApiTypes.UpdateProjectDetailsPayloadProps = {
      archivingState: isProjectActive ? "ARCHIVED" : "UNARCHIVED",
    };

    void dispatch(updateProjectDetails({ projectId: project.id, payload }));

    // TODO: Maybe move adding/removing processing projects to the pending/fulfilled in project-slice
    // Remove projectId from processing project to stop the loading spinner in the project
    dispatch(removeProcessingProjects([project.id]));
    return true;
  }

  return false;
}
